import React, { useEffect } from 'react';

import { isFalsy } from '@leapfinance/frontend-commons';
import { useRouter } from 'next/router';

import { useAppSelector } from '@/app/hooks';
import LoanMelaAwarenessPopper from '@/components/Tiering/LoanMelaAwarenessPopper';
import { selectUserDetails } from '@/features/user/userSlice';
import { Permissions } from '@/permissions/featuresList';
import { trackClick } from '@/segment/eventTracker';
import { useLazyCheckCspPendingPaymentsQuery } from '@/services/cspPerformanceApi';
import { useGetRewardCreditedEventQuery } from '@/services/rewardCredited';
import { PageNames, WidgetFormat, WidgetName } from '@/types/segmentEvents';
import { localStorageKeys } from '@/utils/GBLocalStorageUtil';
import { shouldShowPopup } from '@/utils/newsletterFeatureUtil';

import { MODAL_TYPES } from './Modals/GlobalModal';
import useModal from './Modals/useModal';

const EXTENDED_UK_APPLICATION_BOOSTER_POPUP_DISABLE_DATE = `2025-06-20 23:59:59`;

const routesWhereToNotShow = [
  `/`,
  `/login`,
  `/changepwd`,
  `/resetpwd`,
  `/test-prep/video-course`,
  `/test-prep/video-course/[slug]`,
  `/test-prep/mock-test`,
  `/test-prep/mock-test/[mock_test_id]`,
  `/test-prep/mock-test/result/[mock_test_id]`,
  `/test-prep/mock-test/speaking/result/[id]`,
  `/ielts`,
  `/student-login`,
  `/cas/[prepType]`,
  `/cas/practice/[examId]`,
  `/cas/practice/result/[examId]`,
  `/cas/mock-test/[examId]`,
  `/cas/mock-test/result/[examId]`,
];

const AnnouncementWrapper = (props: { children: React.ReactNode }) => {
  const { showModal } = useModal();
  const router = useRouter();
  const { data: rewardCreditedEventData } = useGetRewardCreditedEventQuery(
    undefined,
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const [triggerCheckPendingPayment, { data: pendingPayment }] =
    useLazyCheckCspPendingPaymentsQuery();

  const cspProfileData = useAppSelector((state) => selectUserDetails(state));

  const isInternationalCsp = cspProfileData?.cspZone === `International`;
  const isCspDD = cspProfileData?.cspZone === `DD`;

  const isStudentUser = cspProfileData?.permissions?.includes(
    Permissions.STUDENT,
  );

  const isAdminUser = cspProfileData?.permissions?.includes(Permissions.ADMIN);

  useEffect(() => {
    const checkPendingPayment = async () => {
      if (router?.pathname?.toString() === `/summary-dashboard`) {
        await triggerCheckPendingPayment();
      }
    };

    checkPendingPayment();
  }, []);

  useEffect(() => {
    if (isFalsy(cspProfileData) || isStudentUser || !router?.isReady) return;

    if (routesWhereToNotShow?.includes(router?.pathname?.toString())) return;

    if (
      rewardCreditedEventData?.data?.includes(`PAYMENT_APPLICATION`) ||
      rewardCreditedEventData?.data?.includes(`STI_APPLICATION`)
    ) {
      showModal({
        modalType: MODAL_TYPES.REWARDS_NOTIFY_MODAL,
        modalProps: {
          type: rewardCreditedEventData?.data?.includes(`PAYMENT_APPLICATION`)
            ? `payment`
            : rewardCreditedEventData?.data?.includes(`STI_APPLICATION`)
            ? `application`
            : null,
        },
      });
    }

    const showUkApplicationBoosterPopup =
      !isInternationalCsp &&
      !isCspDD &&
      isAdminUser &&
      shouldShowPopup({
        key: localStorageKeys.EXTENDED_UK_APPLICATION_BOOSTER_POPUP_DISPLAY_FALL_INTAKE,
        skipTimeSlot: false,
        modalDisableDateTime:
          EXTENDED_UK_APPLICATION_BOOSTER_POPUP_DISABLE_DATE,
        maxTimesToShow: 3,
        maxTimesToShowPerDay: 2,
        minGapHours: 3,
      });

    if (
      showUkApplicationBoosterPopup &&
      !routesWhereToNotShow?.includes(router?.pathname?.toString())
    ) {
      showModal({
        modalType: MODAL_TYPES.NO_RESULTS,
        modalProps: {
          body: (
            <LoanMelaAwarenessPopper
              redirectionUrl={``}
              imageUrl={`https://ik.imagekit.io/v8frh8b7rf/WhatsApp%20Image%202025-06-16%20at%2012.20.43%20PM.jpeg?updatedAt=1750072834259`}
              storageKey={
                localStorageKeys.EXTENDED_UK_APPLICATION_BOOSTER_POPUP_DISPLAY_FALL_INTAKE
              }
              containerClassName="!w-fit"
              hideImageClick
              imageClassName="h-full object-contain"
              imageContainerClassName="!w-fit"
              onModalClose={() => {
                trackClick({
                  pageName: PageNames.UK_APPLICATION_BOOSTER_POPUP,
                  contentFormat: `Modal`,
                  contentName: `Close`,
                  widgetFormat: WidgetFormat.MODAL,
                  widgetName: WidgetName.UK_APPLICATION_BOOSTER_POPUP,
                });
              }}
            />
          ),
        },
      });
    }
  }, [router, cspProfileData, pendingPayment, rewardCreditedEventData]);

  return <>{props.children}</>;
};

export default AnnouncementWrapper;
